import type { ElementErrorType } from "@v1/utils/element";
import type { EditorRoute } from "@v2/utils/router";
import type {
  ReploElement,
  ReploPartialElement,
} from "schemas/generated/element";

import * as React from "react";

import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { Modal } from "@v1/components/designSystem/modal/Modal";
import {
  prepareComponentTemplate,
  VERTICAL_CONTAINER_COMPONENT_TEMPLATE,
} from "@v1/components/editor/defaultComponentTemplates";
import SectionElementEditor from "@v1/components/editor/elementEditors/SectionElementEditor";
import { ElementEditorDataContext } from "@v1/contexts/ElementEditorDataContext";
import {
  useElementEditorErrorContext,
  withElementEditorErrorContext,
} from "@v1/contexts/ElementEditorErrorContext";
import { useElementValidation } from "@v1/hooks/element";
import { useCreateElementMutation } from "@v1/hooks/element/useCreateElementMutation";
import {
  selectComponentDataMapping,
  selectDraftComponent,
} from "@v1/reducers/core-reducer";
import { useEditorSelector, useEditorStore } from "@v1/store/store";
import { elementFormHasErrors } from "@v1/utils/element";
import { analytics } from "@v2/features/infra/analytics/tracking";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useModal } from "@v2/store/modalStore";
import { routes } from "@v2/utils/router";
import { generatePath, useLocation, useNavigate } from "react-router-dom";

export const ExportToSectionModal = withElementEditorErrorContext(() => {
  const {
    element,
    onChangeElement,
    exportToSection,
    validateElement,
    isExporting,
  } = useElementToExport();

  const modal = useModal();
  const navigate = useNavigate();
  const { search } = useLocation();
  const draftComponent = useEditorSelector(selectDraftComponent);
  const { setErrors, clearErrors, errorMapping } =
    useElementEditorErrorContext();

  async function onSubmit() {
    clearErrors("path", "allErrorsFromAllFormKeys");

    const { isValid, errors: validationErrors } = validateElement(element);
    if (!isValid) {
      if (validationErrors.title.length > 0) {
        setErrors("title", validationErrors.title);
      }
      if (validationErrors.path.length > 0) {
        setErrors("path", validationErrors.path);
      }
      return;
    }

    const result = await exportToSection(element);

    if (result.isExported) {
      modal.closeModal({ type: "exportToSectionModal" });
      navigate(
        `${generatePath(routes.editor.element, {
          projectId: result.data!.element.projectId,
          elementId: result.data!.element.id,
        } as EditorRoute)}${search}`,
      );
    } else {
      if (result.errors && result.errors.path.length > 0) {
        setErrors("path", result.errors.path);
      }
    }
  }

  if (!draftComponent) {
    return null;
  }

  return (
    <ElementEditorDataContext.Provider value={{ element, onChangeElement }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (isExporting) {
            return;
          }
          void onSubmit();
        }}
      >
        <Modal
          isOpen={true}
          onOpenChange={(open) => {
            if (!open) {
              modal.closeModal({ type: "exportToSectionModal" });
            }
          }}
          title="Export To Section"
          footer={
            <DeprecatedButton
              size="base"
              variant="primary"
              type="submit"
              isLoading={isExporting}
              disabled={elementFormHasErrors(errorMapping)}
            >
              Export To Section
            </DeprecatedButton>
          }
        >
          <SectionElementEditor requestType="new" />
        </Modal>
      </form>
    </ElementEditorDataContext.Provider>
  );
});

/**
 * This hook handles creation, validation and the state of a new Replo element.
 */
function useElementToExport() {
  const store = useEditorStore();
  const projectId = useCurrentProjectId();

  const [element, setElement] = React.useState<ReploPartialElement>({
    name: "",
    projectId: projectId ?? "",
    type: "shopifySection",
    hideDefaultHeader: false,
    hideDefaultFooter: false,
    hideShopifyAnnouncementBar: false,
    shopifyPagePath: "",
    shopifyTemplateSuffix: "",
    isHomepage: false,
    isPublished: false,
    shopifyMetafields: [],
    shopifyArticleImage: { src: "" },
  });

  const validateElement = useElementValidation("shopifySection");
  const { createElement, isLoading } = useCreateElementMutation();

  const onExportToSection = React.useCallback(
    async function onExportToSectionFn(element: ReploPartialElement): Promise<
      | {
          isExported: true;
          data: { element: ReploElement };
        }
      | {
          isExported: false;
          errors?: { path: ElementErrorType[] };
        }
    > {
      const state = store.getState();
      const draftComponent = selectDraftComponent(state);
      if (draftComponent && projectId) {
        const componentDataMapping = selectComponentDataMapping(state);
        try {
          const response = await createElement({
            ...element,
            component: prepareComponentTemplate(
              {
                ...VERTICAL_CONTAINER_COMPONENT_TEMPLATE,
                template: {
                  ...VERTICAL_CONTAINER_COMPONENT_TEMPLATE.template!,
                  children: [draftComponent],
                },
              },
              null,
              null,
              {
                componentDataMapping,
                getAttribute: null,
                productResolutionDependencies: null,
                context: null,
              },
            ),
          });

          if ("key" in response && response.key === "pathAlreadyInUse") {
            return {
              isExported: false,
              errors: { path: ["pathAlreadyExists"] },
            };
          }

          if (response.element?.id) {
            analytics.logEvent("element.new", {
              operation: "create",
              type: "shopifySection",
              creationMethod: "exportToSection",
            });
            return {
              isExported: true,
              data: {
                element: response.element,
              },
            };
          }
        } catch {
          return {
            isExported: false,
          };
        }
      }

      return { isExported: false };
    },
    [createElement, projectId, store],
  );

  return {
    element,
    onChangeElement: setElement,
    exportToSection: onExportToSection,
    validateElement,
    isExporting: isLoading,
  };
}
